import { todayCalendarDayKey, toCalendarDayKey } from '../utils/assetBookingCalendar';
import { normalizeVoiceText, removeVoicePhrase } from './matchVoiceSearchCity';

export type AssistantWhen = {
  date: string | null;
  time: string | null;
  datePhrase: string | null;
  timePhrase: string | null;
  /** Set when the utterance has an explicit from–to clock range. */
  durationMinutes: number | null;
  /**
   * Soft month window `YYYY-MM` from “בחודש דצמבר” / “in December”.
   * Not a single booking day — any day in that month.
   */
  month: string | null;
  monthPhrase: string | null;
};

const WEEKDAYS_EN = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

const WEEKDAYS_HE: Array<{ phrase: string; weekday: number }> = [
  { phrase: 'יום ראשון', weekday: 0 },
  { phrase: 'יום שני', weekday: 1 },
  { phrase: 'יום שלישי', weekday: 2 },
  { phrase: 'יום רביעי', weekday: 3 },
  { phrase: 'יום חמישי', weekday: 4 },
  { phrase: 'יום שישי', weekday: 5 },
  { phrase: 'יום שבת', weekday: 6 },
  { phrase: 'בשבת', weekday: 6 },
  { phrase: 'שבת', weekday: 6 },
];

/** Index = JS weekday (0 = Sunday). */
const HE_WEEKDAY_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

/** "the upcoming / next / this" after a Hebrew weekday — part of the date, not a search word. */
const HE_UPCOMING_QUALIFIERS = 'הקרוב|הקרובה|הבא|הבאה|הזה|הזאת';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function addCalendarDays(now: Date, days: number): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  return toCalendarDayKey(d.getFullYear(), d.getMonth(), d.getDate());
}

function nextWeekdayKey(now: Date, weekday: number): string {
  const current = now.getDay();
  const delta = (weekday - current + 7) % 7;
  return addCalendarDays(now, delta);
}

function parseHourMinute(hourRaw: string, minuteRaw: string, ampm: string | undefined): string | null {
  let hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || minute < 0 || minute > 59) {
    return null;
  }
  const mer = ampm?.toLowerCase();
  if (mer === 'pm' && hour < 12) {
    hour += 12;
  } else if (mer === 'am' && hour === 12) {
    hour = 0;
  }
  if (hour < 0 || hour > 23) {
    return null;
  }
  return `${pad2(hour)}:${pad2(minute)}`;
}

/** Longest first so "אחת עשרה" wins over "אחת". */
const HE_HOUR_WORDS: Array<[string, number]> = [
  ['אחת עשרה', 11],
  ['אחד עשרה', 11],
  ['שתים עשרה', 12],
  ['שתיים עשרה', 12],
  ['אחת', 1],
  ['שתיים', 2],
  ['שתים', 2],
  ['שלוש', 3],
  ['ארבע', 4],
  ['חמש', 5],
  ['שש', 6],
  ['שבע', 7],
  ['שמונה', 8],
  ['תשע', 9],
  ['עשר', 10],
];

const EN_HOUR_WORDS: Array<[string, number]> = [
  ['one', 1],
  ['two', 2],
  ['three', 3],
  ['four', 4],
  ['five', 5],
  ['six', 6],
  ['seven', 7],
  ['eight', 8],
  ['nine', 9],
  ['ten', 10],
  ['eleven', 11],
  ['twelve', 12],
];

type DayPart = 'morning' | 'noon' | 'afternoon' | 'night';

const HE_DAY_PARTS: Array<[string, DayPart]> = [
  ['אחרי הצהריים', 'afternoon'],
  ['אחר הצהריים', 'afternoon'],
  ['אחה"צ', 'afternoon'],
  ['אחה״צ', 'afternoon'],
  ['אחהצ', 'afternoon'],
  ['לפנות ערב', 'afternoon'],
  ['בבוקר', 'morning'],
  ['בצהריים', 'noon'],
  ['בצהרים', 'noon'],
  ['בערב', 'afternoon'],
  ['בלילה', 'night'],
];

const EN_DAY_PARTS: Array<[string, DayPart]> = [
  ['in the morning', 'morning'],
  ['in the afternoon', 'afternoon'],
  ['in the evening', 'afternoon'],
  ['at night', 'night'],
  ['tonight', 'night'],
];

/** "ב-20 שקל", "ב-3 שעות", "ל-10 אנשים" are prices, durations or head counts, not clock times. */
const HE_NOT_TIME_FOLLOWERS = 'שקל|ש"ח|ש״ח|שח|₪|אנשים|איש|משתתפים|מקומות|שעות|דקות|ימים';
const EN_NOT_TIME_FOLLOWERS = 'nis|shekels?|ils|₪|people|persons|guests|hours?|minutes?|days?';

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 24h hour for a spoken 1–12 hour. Without a day part, bookings mean business
 * hours: 1–6 → afternoon, 7–11 → morning ("בשלוש" = 15:00).
 */
function resolveSpokenHour(hour: number, part: DayPart | null): number | null {
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
    return null;
  }
  if (hour === 0 || hour >= 13) {
    return hour;
  }
  switch (part) {
    case 'morning':
      return hour === 12 ? 0 : hour;
    case 'noon':
      return hour <= 6 ? hour + 12 : hour;
    case 'afternoon':
      return hour === 12 ? 12 : hour + 12;
    case 'night':
      if (hour === 12) {
        return 0;
      }
      return hour >= 7 ? hour + 12 : hour;
    default:
      return hour <= 6 ? hour + 12 : hour;
  }
}

/** "בשלוש", "בשעה 3 וחצי", "ב-10 בבוקר", "at 3", "at three in the afternoon". */
function matchSpokenTime(hay: string): { time: string; phrase: string } | null {
  const heWords = HE_HOUR_WORDS.map(([word]) => escapeRegExp(word)).join('|');
  const heParts = HE_DAY_PARTS.map(([part]) => escapeRegExp(part)).join('|');
  const he = hay.match(
    new RegExp(
      `(?:^|\\s)((?:בשעה\\s+|ב\\s?)(${heWords}|\\d{1,2})(?:\\s+(וחצי|ורבע))?(?:\\s+(${heParts}))?)(?!\\s*(?:${HE_NOT_TIME_FOLLOWERS}))(?=\\s|$|[?.!,])`,
    ),
  );
  if (he?.[1] && he[2]) {
    const word = HE_HOUR_WORDS.find(([w]) => w === he[2]);
    const hourRaw = word ? word[1] : Number(he[2]);
    const part = HE_DAY_PARTS.find(([p]) => p === he[4])?.[1] ?? null;
    const hour = resolveSpokenHour(hourRaw, part);
    if (hour !== null) {
      const minute = he[3] === 'וחצי' ? 30 : he[3] === 'ורבע' ? 15 : 0;
      return { time: `${pad2(hour)}:${pad2(minute)}`, phrase: he[1].trim() };
    }
  }

  const enWords = EN_HOUR_WORDS.map(([word]) => word).join('|');
  const enParts = EN_DAY_PARTS.map(([part]) => escapeRegExp(part)).join('|');
  const en = hay.match(
    new RegExp(
      `\\b(at\\s+(${enWords}|\\d{1,2})(?:\\s+(thirty|fifteen))?(?:\\s*o'?clock)?(?:\\s+(${enParts}))?)(?!\\s*(?:${EN_NOT_TIME_FOLLOWERS}))(?=\\s|$|[?.!,])`,
      'i',
    ),
  );
  if (en?.[1] && en[2]) {
    const lower = en[2].toLowerCase();
    const word = EN_HOUR_WORDS.find(([w]) => w === lower);
    const hourRaw = word ? word[1] : Number(en[2]);
    const part = EN_DAY_PARTS.find(([p]) => p === en[4]?.toLowerCase())?.[1] ?? null;
    const hour = resolveSpokenHour(hourRaw, part);
    if (hour !== null) {
      const minute = en[3]?.toLowerCase() === 'thirty' ? 30 : en[3]?.toLowerCase() === 'fifteen' ? 15 : 0;
      return { time: `${pad2(hour)}:${pad2(minute)}`, phrase: en[1].trim() };
    }
  }
  return null;
}

function matchTime(text: string): { time: string; phrase: string } | null {
  const hay = normalizeVoiceText(text);
  if (!hay) {
    return null;
  }
  const patterns: RegExp[] = [
    /(?:בשעה|at)\s+(\d{1,2})[:.](\d{2})(?:\s*(a\.?m\.?|p\.?m\.?))?/i,
    /\b(\d{1,2})[:.](\d{2})\s*(a\.?m\.?|p\.?m\.?)\b/i,
    /(?:בשעה|at)\s+(\d{1,2})\s*(a\.?m\.?|p\.?m\.?)/i,
    /\b(\d{1,2})\s*(a\.?m\.?|p\.?m\.?)\b/i,
    /(?:בשעה|at)\s+(\d{1,2})[:.](\d{2})/i,
    /\b([01]?\d|2[0-3])[:.]([0-5]\d)\b/,
  ];
  for (const pattern of patterns) {
    const matched = hay.match(pattern);
    if (!matched?.[0]) {
      continue;
    }
    const ampm = (matched[3] || matched[2] || '').replace(/\./g, '');
    const looksAmpm = /^(am|pm)$/i.test(ampm);
    let time: string | null = null;
    if (matched[2] && /^\d{2}$/.test(matched[2]) && !looksAmpm) {
      time = parseHourMinute(matched[1], matched[2], matched[3]);
    } else if (looksAmpm) {
      const minute = matched[2] && /^\d{2}$/.test(matched[2]) ? matched[2] : '00';
      const mer = matched[3] && /^(a|p)/i.test(matched[3]) ? matched[3] : matched[2];
      time = parseHourMinute(matched[1], minute, mer);
    }
    if (time) {
      return { time, phrase: matched[0] };
    }
  }
  return matchSpokenTime(hay);
}

function matchAbsoluteNumericDate(text: string): { date: string; phrase: string } | null {
  const hay = normalizeVoiceText(text);
  if (!hay) {
    return null;
  }
  const iso = hay.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { date: `${iso[1]}-${iso[2]}-${iso[3]}`, phrase: iso[0] };
    }
  }
  // Israeli / European: 18.11.2026 or 18/11/26 — must win over HH.MM time parsing.
  const dmy = hay.match(/\b(\d{1,2})[./](\d{1,2})[./](\d{2,4})\b/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    let year = Number(dmy[3]);
    if (year < 100) {
      year += 2000;
    }
    if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { date: `${year}-${pad2(month)}-${pad2(day)}`, phrase: dmy[0] };
    }
  }
  return null;
}

/** "1200" / "12:00" / "9:30" → HH:MM */
function parseFlexibleClockToken(raw: string): string | null {
  const token = raw.trim();
  const colon = token.match(/^(\d{1,2}):(\d{2})$/);
  if (colon) {
    return parseHourMinute(colon[1], colon[2], undefined);
  }
  if (/^\d{3,4}$/.test(token)) {
    const padded = token.padStart(4, '0');
    return parseHourMinute(padded.slice(0, 2), padded.slice(2), undefined);
  }
  return null;
}

/**
 * "משעה 1200 עד 1800" / "בין השעות 10:00 עד 16:00" / "from 12:00 to 18:00" —
 * start time + duration, strip as one phrase so price parsing does not treat
 * "עד 1200" as ≤ ₪1200, and "בין השעות" never becomes a venue keyword.
 */
function matchTimeRange(text: string): {
  time: string;
  phrase: string;
  durationMinutes: number;
} | null {
  const hay = normalizeVoiceText(text);
  if (!hay) {
    return null;
  }
  const patterns: RegExp[] = [
    // Longer Hebrew “between the hours” first so the prefix is stripped with the times.
    /בין\s+השעות?\s+(\d{1,2}:\d{2}|\d{3,4})\s*(?:עד|עד־|עד-|to|-|–|—)\s*(\d{1,2}:\d{2}|\d{3,4})/i,
    /between\s+(?:the\s+)?hours?(?:\s+of)?\s+(\d{1,2}:\d{2}|\d{3,4})\s*(?:and|to|until|-|–|—)\s*(\d{1,2}:\d{2}|\d{3,4})/i,
    /(?:משעה|מ־|מ-)?\s*(\d{1,2}:\d{2}|\d{3,4})\s*(?:עד|עד־|עד-|to|-|–|—)\s*(\d{1,2}:\d{2}|\d{3,4})/i,
    /\bfrom\s+(\d{1,2}:\d{2}|\d{3,4})\s*(?:to|until|-|–|—)\s*(\d{1,2}:\d{2}|\d{3,4})\b/i,
  ];
  for (const pattern of patterns) {
    const matched = hay.match(pattern);
    if (!matched?.[0] || !matched[1] || !matched[2]) {
      continue;
    }
    const start = parseFlexibleClockToken(matched[1]);
    const end = parseFlexibleClockToken(matched[2]);
    if (!start || !end) {
      continue;
    }
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const startMin = sh * 60 + sm;
    const endMin = eh * 60 + em;
    if (endMin <= startMin) {
      continue;
    }
    return {
      time: start,
      phrase: matched[0].trim(),
      durationMinutes: endMin - startMin,
    };
  }
  return null;
}

const MONTH_NAMES: Array<{ phrases: string[]; monthIndex: number }> = [
  { monthIndex: 0, phrases: ['january', 'ינואר'] },
  { monthIndex: 1, phrases: ['february', 'פברואר'] },
  { monthIndex: 2, phrases: ['march', 'מרץ', 'מארס'] },
  { monthIndex: 3, phrases: ['april', 'אפריל'] },
  { monthIndex: 4, phrases: ['may', 'מאי'] },
  { monthIndex: 5, phrases: ['june', 'יוני'] },
  { monthIndex: 6, phrases: ['july', 'יולי'] },
  { monthIndex: 7, phrases: ['august', 'אוגוסט'] },
  { monthIndex: 8, phrases: ['september', 'ספטמבר'] },
  { monthIndex: 9, phrases: ['october', 'אוקטובר'] },
  { monthIndex: 10, phrases: ['november', 'נובמבר'] },
  { monthIndex: 11, phrases: ['december', 'דצמבר'] },
];

/** Next occurrence of a calendar month as `YYYY-MM` (any day in that month). */
function matchMonthName(text: string, now: Date): { month: string; phrase: string } | null {
  const hay = normalizeVoiceText(text);
  if (!hay) {
    return null;
  }
  for (const entry of MONTH_NAMES) {
    for (const name of entry.phrases) {
      const he = new RegExp(
        `(?:ב(?:חודש\\s+)?|לחודש\\s+|בחודש\\s+|in\\s+(?:the\\s+)?(?:month\\s+of\\s+)?)?(${escapeRegExp(name)})(?:\\s+(\\d{4}))?`,
        'i',
      );
      const matched = hay.match(he);
      if (!matched?.[0] || !matched[1]) {
        continue;
      }
      const explicitYear = matched[2] ? Number(matched[2]) : null;
      let year = now.getFullYear();
      if (explicitYear && explicitYear >= 1900 && explicitYear <= 2100) {
        year = explicitYear;
      } else if (entry.monthIndex < now.getMonth()) {
        year += 1;
      }
      return {
        month: `${year}-${pad2(entry.monthIndex + 1)}`,
        phrase: matched[0].trim(),
      };
    }
  }
  return null;
}

/** First calendar day of a `YYYY-MM` month window (for opening the booking calendar). */
export function firstDayOfAssistantMonth(monthYm: string): string | null {
  const m = /^(\d{4})-(\d{2})$/.exec(monthYm.trim());
  if (!m) {
    return null;
  }
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  if (!Number.isFinite(year) || monthIndex < 0 || monthIndex > 11) {
    return null;
  }
  return toCalendarDayKey(year, monthIndex, 1);
}

function matchDate(text: string, now: Date): { date: string; phrase: string } | null {
  const hay = normalizeVoiceText(text);
  if (!hay) {
    return null;
  }
  const absolute = matchAbsoluteNumericDate(hay);
  if (absolute) {
    return absolute;
  }
  if (/(day after tomorrow|מחרתיים)/i.test(hay)) {
    const phrase = /day after tomorrow/i.test(hay) ? 'day after tomorrow' : 'מחרתיים';
    return { date: addCalendarDays(now, 2), phrase };
  }
  if (/\btomorrow\b|מחר(?!תיים)/i.test(hay)) {
    const phrase = /\btomorrow\b/i.test(hay) ? 'tomorrow' : 'מחר';
    return { date: addCalendarDays(now, 1), phrase };
  }
  if (/\btoday\b|היום/i.test(hay)) {
    const phrase = /\btoday\b/i.test(hay) ? 'today' : 'היום';
    return { date: todayCalendarDayKey(now), phrase };
  }
  const qualifiedEn = hay.match(
    new RegExp(
      `\\b(?:on\\s+)?(?:this\\s+coming|this|next|coming|upcoming)\\s+(${WEEKDAYS_EN.join('|')})\\b`,
      'i',
    ),
  );
  if (qualifiedEn?.[1]) {
    const weekday = WEEKDAYS_EN.indexOf(
      qualifiedEn[1].toLowerCase() as (typeof WEEKDAYS_EN)[number],
    );
    if (weekday >= 0) {
      return { date: nextWeekdayKey(now, weekday), phrase: qualifiedEn[0] };
    }
  }
  const qualifiedHe = hay.match(
    new RegExp(`[בל]?(?:יום\\s+)?(${HE_WEEKDAY_NAMES.join('|')})\\s+(?:${HE_UPCOMING_QUALIFIERS})`),
  );
  if (qualifiedHe?.[1]) {
    return {
      date: nextWeekdayKey(now, HE_WEEKDAY_NAMES.indexOf(qualifiedHe[1])),
      phrase: qualifiedHe[0],
    };
  }
  const onEn = hay.match(new RegExp(`\\bon\\s+(${WEEKDAYS_EN.join('|')})\\b`, 'i'));
  if (onEn?.[1]) {
    const weekday = WEEKDAYS_EN.indexOf(onEn[1].toLowerCase() as (typeof WEEKDAYS_EN)[number]);
    if (weekday >= 0) {
      return { date: nextWeekdayKey(now, weekday), phrase: onEn[0] };
    }
  }
  const bareEn = hay.match(new RegExp(`\\b(${WEEKDAYS_EN.join('|')})\\b`, 'i'));
  if (bareEn?.[1]) {
    const weekday = WEEKDAYS_EN.indexOf(bareEn[1].toLowerCase() as (typeof WEEKDAYS_EN)[number]);
    if (weekday >= 0) {
      return { date: nextWeekdayKey(now, weekday), phrase: bareEn[0] };
    }
  }
  for (const entry of WEEKDAYS_HE) {
    if (hay.includes(entry.phrase)) {
      return { date: nextWeekdayKey(now, entry.weekday), phrase: entry.phrase };
    }
  }
  return null;
}

/** Parse relative dates and clock times from a HE/EN assistant utterance. */
export function parseAssistantWhen(text: string, now: Date = new Date()): AssistantWhen {
  let remainder = normalizeVoiceText(text);
  // Absolute dates before HH.MM so "18.11.2026" is not read as 18:11.
  const absoluteDate = matchAbsoluteNumericDate(remainder);
  if (absoluteDate) {
    remainder = removeVoicePhrase(remainder, absoluteDate.phrase);
  }
  // Month window before weekday/relative dates so “דצמבר” is not leftover venue text.
  const monthHit = absoluteDate ? null : matchMonthName(remainder, now);
  if (monthHit) {
    remainder = removeVoicePhrase(remainder, monthHit.phrase);
  }
  const rangeHit = matchTimeRange(remainder);
  let timeHit: { time: string; phrase: string } | null = rangeHit
    ? { time: rangeHit.time, phrase: rangeHit.phrase }
    : null;
  if (rangeHit) {
    remainder = removeVoicePhrase(remainder, rangeHit.phrase);
  } else {
    timeHit = matchTime(remainder);
    if (timeHit) {
      remainder = removeVoicePhrase(remainder, timeHit.phrase);
    }
  }
  const dateHit =
    absoluteDate ?? matchDate(remainder, now) ?? matchDate(text, now);
  return {
    date: dateHit?.date ?? null,
    time: timeHit?.time ?? null,
    datePhrase: dateHit?.phrase ?? null,
    timePhrase: timeHit?.phrase ?? null,
    durationMinutes: rangeHit?.durationMinutes ?? null,
    month: monthHit?.month ?? null,
    monthPhrase: monthHit?.phrase ?? null,
  };
}

/** Booking hour pickers are whole hours; 14:30 becomes 14:00. */
export function floorAssistantTimeToHour(timeHm: string): string | null {
  const m = /^(\d{2}):(\d{2})$/.exec(timeHm.trim());
  if (!m) {
    return null;
  }
  return `${m[1]}:00`;
}

export function stripAssistantWhenPhrases(text: string, when: AssistantWhen): string {
  let out = normalizeVoiceText(text);
  if (when.timePhrase) {
    out = removeVoicePhrase(out, when.timePhrase);
  }
  if (when.datePhrase) {
    out = removeVoicePhrase(out, when.datePhrase);
  }
  if (when.monthPhrase) {
    out = removeVoicePhrase(out, when.monthPhrase);
  }
  // Orphan time-range / date wrappers if a shorter pattern matched without them.
  for (const filler of [
    'בין השעות',
    'בין השעה',
    'between the hours',
    'between hours',
    'ביום',
    'הקרוב',
    'הבא',
    'this coming',
    'coming',
    'upcoming',
  ]) {
    out = removeVoicePhrase(out, filler);
  }
  return out;
}
